import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { RvoMeldingeenheidDetails, RvoUbnInfoService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { RvoVerblijfPlaats } from './rvo-animal.models';
import { bedrijfstypeLabel, formatRvoDisplayDate } from './animal-info.utils';

interface RouteStop {
  ubn: string;
  naam: string | null;
  address: string | null;
  typeBedrijfsvestiging: string | null;
  typeLabel: string;
  aanvoerLabel: string;
  afvoerLabel: string;
  isStillPresent: boolean;
  lat: number | null;
  lng: number | null;
  stopIndex: number;
  totalStops: number;
}

@Component({
  selector: 'tcc-mono-rvo-animal-route-map-dialog',
  templateUrl: './rvo-animal-route-map-dialog.component.html',
  styleUrls: ['./rvo-animal-route-map-dialog.component.scss']
})
export class RvoAnimalRouteMapDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly _rvoUbnInfo = inject(RvoUbnInfoService);
  private readonly _modalCtrl = inject(ModalController);

  @Input() public places: RvoVerblijfPlaats[] = [];
  @Input() public dierLandcode: string | null = null;
  @Input() public dierLevensnummer: string | null = null;

  @ViewChild('mapContainer') private mapContainer!: ElementRef<HTMLDivElement>;

  public loading = true;
  public error: string | null = null;
  public skippedCount = 0;
  public resolvedStops: RouteStop[] = [];
  public activeStopIndex: number | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private leafletMap: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private markerMap = new Map<number, any>();
  private pendingStops: RouteStop[] | null = null;
  private viewReady = false;

  public get dierDisplayId(): string {
    const lc = this.dierLandcode?.trim() ?? '';
    const nr = this.dierLevensnummer?.trim() ?? '';
    return `${lc}${nr}`.trim() || '—';
  }

  public ngOnInit(): void {
    const sorted = [...(this.places ?? [])].sort((a, b) => {
      const aanvoerDiff = this._parseRvoDateMs(a.aanvoerDatumME) - this._parseRvoDateMs(b.aanvoerDatumME);
      if (aanvoerDiff !== 0) {
        return aanvoerDiff;
      }
      const aAfvoer = a.afvoerDatumME ? this._parseRvoDateMs(a.afvoerDatumME) : Infinity;
      const bAfvoer = b.afvoerDatumME ? this._parseRvoDateMs(b.afvoerDatumME) : Infinity;
      return aAfvoer - bAfvoer;
    });

    const uniqueUbns = [
      ...new Set(
        sorted
          .map((p) => p.meldingeenheid?.trim())
          .filter((ubn): ubn is string => !!ubn),
      ),
    ];

    if (uniqueUbns.length === 0) {
      this.loading = false;
      this.error = 'Geen UBN-nummers gevonden in de verblijfplaatsen.';
      return;
    }

    const leafletPromise = this._loadLeaflet();
    const obsArray: Observable<RvoMeldingeenheidDetails | null>[] = uniqueUbns.map((ubn) =>
      this._rvoUbnInfo.ubnInfo(ubn).pipe(
        map((items) => items[0] ?? null),
        catchError(() => of(null)),
      ),
    );

    forkJoin(obsArray).subscribe({
      next: (results) => {
        const ubnInfoMap = new Map<string, RvoMeldingeenheidDetails | null>(
          uniqueUbns.map((ubn, i) => [ubn, results[i]]),
        );

        const stops: RouteStop[] = sorted.map((plaats, index) => {
          const ubn = plaats.meldingeenheid?.trim() ?? '';
          return this._buildStop(plaats, ubnInfoMap.get(ubn) ?? null, index, sorted.length);
        });

        this.skippedCount = stops.filter((s) => s.lat === null || s.lng === null).length;
        this.resolvedStops = stops;
        this.loading = false;

        leafletPromise
          .then(() => setTimeout(() => this._onReadyToRender(stops), 0))
          .catch(() => {
            this.error = 'Kaartbibliotheek kon niet worden geladen.';
          });
      },
      error: () => {
        this.loading = false;
        this.error = 'Verblijfplaats-gegevens konden niet worden opgehaald bij RVO.';
      },
    });
  }

  public ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.pendingStops) {
      this._initMap(this.pendingStops);
      this.pendingStops = null;
    }
  }

  public ngOnDestroy(): void {
    if (this.leafletMap) {
      this.leafletMap.remove();
      this.leafletMap = null;
    }
  }

  public close(): void {
    void this._modalCtrl.dismiss();
  }

  public hasValidStops(): boolean {
    return this.skippedCount < (this.places?.length ?? 0);
  }

  public stopColor(stop: RouteStop): string {
    const isFirst = stop.stopIndex === 0;
    const isLast = stop.stopIndex === stop.totalStops - 1;
    if (stop.lat === null) {
      return '#94a3b8';
    }
    return isFirst ? '#22c55e' : isLast ? '#f97316' : '#3b82f6';
  }

  public stopCardClass(stop: RouteStop): string {
    const isActive = this.activeStopIndex === stop.stopIndex;
    const noCoords = stop.lat === null;

    if (noCoords) {
      return 'route-stop route-stop--disabled';
    }
    if (isActive) {
      return 'route-stop route-stop--active';
    }
    return 'route-stop';
  }

  public focusStop(stop: RouteStop): void {
    if (stop.lat === null || stop.lng === null || !this.leafletMap) {
      return;
    }

    this.activeStopIndex = stop.stopIndex;
    this.leafletMap.setView([stop.lat, stop.lng], 14, { animate: true });

    const marker = this.markerMap.get(stop.stopIndex);
    if (marker) {
      marker.openPopup();
    }
  }

  private _onReadyToRender(stops: RouteStop[]): void {
    if (this.viewReady) {
      this._initMap(stops);
    } else {
      this.pendingStops = stops;
    }
  }

  private _buildStop(
    plaats: RvoVerblijfPlaats,
    details: RvoMeldingeenheidDetails | null,
    index: number,
    total: number,
  ): RouteStop {
    const ubn = plaats.meldingeenheid?.trim() ?? '';
    const naam = details?.naamHouder?.trim() ?? plaats.naam?.trim() ?? null;

    const address = details
      ? [details.adresOmsME, details.postcodeME, details.plaatsnaamME]
          .filter((p): p is string => !!p?.trim())
          .join(', ') || null
      : [plaats.adresOmsME, plaats.postcodeOmsME, plaats.woonplaatsME]
          .filter((p): p is string => !!p?.trim())
          .join(', ') || null;

    const isLast = index === total - 1;
    const isStillPresent = isLast && !plaats.afvoerDatumME?.trim();

    return {
      ubn,
      naam,
      address,
      typeBedrijfsvestiging: plaats.typeBedrijfsvestiging,
      typeLabel: bedrijfstypeLabel(plaats.typeBedrijfsvestiging),
      aanvoerLabel: formatRvoDisplayDate(plaats.aanvoerDatumME) || 'Onbekend',
      afvoerLabel: isStillPresent
        ? 'Nog aanwezig'
        : formatRvoDisplayDate(plaats.afvoerDatumME) || 'Onbekend',
      isStillPresent,
      lat: details?.vdvLatitude ?? null,
      lng: details?.vdvLongitude ?? null,
      stopIndex: index,
      totalStops: total,
    };
  }

  private _loadLeaflet(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if ((window as { L?: unknown })['L']) {
        resolve();
        return;
      }

      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(css);

      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Leaflet kon niet worden geladen'));
      document.head.appendChild(script);
    });
  }

  private _initMap(stops: RouteStop[]): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const L = (window as any)['L'];
    if (!L || !this.mapContainer?.nativeElement) {
      return;
    }

    const validStops = stops.filter((s) => s.lat !== null && s.lng !== null);

    if (validStops.length === 0) {
      this.error =
        'Geen van de verblijfplaatsen heeft coördinaten beschikbaar. ' +
        'Mogelijk zijn de UBN-gegevens bij RVO niet compleet.';
      return;
    }

    this.leafletMap = L.map(this.mapContainer.nativeElement, { scrollWheelZoom: true });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(this.leafletMap);

    const latLngs: [number, number][] = validStops.map((s) => [s.lat!, s.lng!]);

    if (latLngs.length > 1) {
      L.polyline(latLngs, { color: '#3b82f6', weight: 3, opacity: 0.7 }).addTo(this.leafletMap);
    }

    const totalStops = stops.length;
    validStops.forEach((stop) => {
      const isFirst = stop.stopIndex === 0;
      const isLast = stop.stopIndex === totalStops - 1;
      const color = isFirst ? '#22c55e' : isLast ? '#f97316' : '#3b82f6';
      const labelNr = stop.stopIndex + 1;

      const iconHtml = `
        <div style="
          width:26px;height:26px;
          background:${color};
          border:3px solid white;
          border-radius:50%;
          box-shadow:0 2px 6px rgba(0,0,0,0.3);
          display:flex;align-items:center;justify-content:center;
          font-size:10px;color:white;font-weight:700;line-height:1;
        ">${labelNr}</div>
      `;

      const icon = L.divIcon({
        html: iconHtml,
        className: '',
        iconSize: [26, 26],
        iconAnchor: [13, 13],
        popupAnchor: [0, -16],
      });

      const marker = L.marker([stop.lat!, stop.lng!], { icon })
        .addTo(this.leafletMap)
        .bindPopup(
          `<strong>${stop.naam ?? 'Onbekende locatie'}</strong><br/>UBN: ${stop.ubn}`,
          { maxWidth: 260 },
        );

      marker.on('click', () => {
        this.activeStopIndex = stop.stopIndex;
      });

      this.markerMap.set(stop.stopIndex, marker);
    });

    if (latLngs.length === 1) {
      this.leafletMap.setView(latLngs[0], 14);
    } else {
      this.leafletMap.fitBounds(latLngs, { padding: [50, 50] });
    }
  }

  private _parseRvoDateMs(value: string | null | undefined): number {
    if (!value?.trim()) {
      return 0;
    }
    const match = value.trim().match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (!match) {
      return 0;
    }
    return new Date(+match[3], +match[2] - 1, +match[1]).getTime();
  }
}
